/**
 * The laptop that lives in the case (it is in the case's picture): its
 * display lit over the picture. On it the Enemy's seal flickers red, the
 * strike that landed. Placed in the case picture's box (open-case.ts
 * SCREEN_BOX), sized in its cqw.
 */
import type { ReactNode } from "react";
import { EnemyMark } from "./strike-page";
import type { Tier } from "@/features/funnel/types";
import { screenFor } from "./open-case";

export function Laptop({
  tier,
  onClick,
  label = "Open the mission file",
  children,
}: {
  /** whose case: where its laptop's display is */
  tier: Tier;
  onClick?: () => void;
  label?: string;
  /** what is on the screen; the Enemy's seal by default */
  children?: ReactNode;
}) {
  const glass = (
    <>
      {children ?? <EnemyScreen />}
      {/* the glass: scanlines and a sheen */}
      <div className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(0deg,rgba(0,0,0,.28)_0_1px,transparent_1px_3px)]" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(160deg,rgba(255,255,255,.08),transparent_45%)]" />
    </>
  );
  // the laptop is in the case's picture: light up its display
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={!onClick}
      style={screenFor(tier)}
      className="absolute cursor-pointer overflow-hidden rounded-[.3cqw] bg-black disabled:cursor-default"
    >
      {glass}
    </button>
  );
}

/** The Enemy's seal on the screen, red, flickering like a bad signal. */
export function EnemyScreen({ still = false }: { still?: boolean }) {
  return (
    <div className="absolute inset-0 grid place-items-center bg-[radial-gradient(ellipse_at_50%_50%,#3a0606,#0a0000_75%)]">
      {/* flickering, as on the case; `still` where it should just sit (HQ) */}
      <EnemyMark
        tone="screen"
        className={`h-[80%] w-auto ${still ? "" : "animate-[smEnemyFlicker_2.4s_steps(1,end)_infinite]"}`}
      />
      <style>{`
        @keyframes smEnemyFlicker{0%{opacity:1}6%{opacity:.25}9%{opacity:1}31%{opacity:.85}33%{opacity:.1}36%{opacity:1}61%{opacity:1}62%{opacity:.4;translate:.6% 0}64%{opacity:1;translate:0 0}88%{opacity:.7}90%{opacity:1}}
      `}</style>
    </div>
  );
}
